/**
 * SUB-4.15 · F-50. What the Stage column is allowed to say.
 *
 * ⚠️ THIS DECIDES WHAT IS SHOWN. IT BOUNDS NOTHING. Same standing rule as
 * `projectListPaging.ts`, `mergedFilters.ts` and `mergedMetrics.ts`: which stages
 * this caller may read is decided by `merged_scope_project_stages()` inside the
 * database, and nothing here may ever be why a name is absent.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE COLUMN DID NOT GO BLANK. IT SAID "Not set", AND "Not set" CANNOT BE
 * TRUE.
 *
 * The list used to embed `stages ( name )`. A PostgREST embed is bounded by the
 * NARROWER of the two policies, and `stages_select` is single-tenant while
 * `projects_select` resolves through merged scope, so a sub-account's project
 * came back with its stage as null. The old cell was:
 *
 *     {project.stages && project.stages[0]?.name || 'Not set'}
 *
 * `&&` binds tighter than `||`, so that fell through to the literal string.
 * Read from `information_schema` on 2026-08-17:
 *
 *     projects.current_stage_id   bigint NOT NULL, FK -> stages(id)
 *     stages.name                 text   NOT NULL
 *
 * A project without a stage name is not a state this schema can represent. So
 * there are THREE cells here and not four: there is no `absent`, and `Not set`
 * appears nowhere in this file.
 *
 * ⚠️ AND IT WAS NEVER ONLY A MERGED-SCOPE DEFECT. `stages_select` withholds on
 * two independent grounds, and the second is the per-stage `permissions` /
 * `user_permissions` arm, which applies inside ONE account. Measured 2026-08-17:
 * **531 live projects (10.9%)** sit in a stage carrying a non-empty permission
 * array, and for a caller without the named role every one of those cells said
 * `Not set` with no sub-account anywhere in the picture. D67 routes single-account
 * scope through the same function, so those rows are fixed by the same change.
 *
 * ---------------------------------------------------------------------------
 * D65 · WITHHELD IS NOT MISSING, AND THE DIFFERENCE IS THE WHOLE POINT.
 *
 * `merged_scope_project_stages` carries the per-stage permission arm (D31's case,
 * not D46's), so merged scope shows exactly the cell the caller would see after
 * switching into that account. When the arm refuses, the function returns
 * `stage_name: null` and `name_withheld: true`, and this module turns that into a
 * statement rather than a silence. NFR8: a quiet wrong answer is worse than a
 * stated boundary.
 */

/** One row of `merged_scope_project_stages(uuid[])`. */
export interface ProjectStageRow {
  project_id: string;
  tenant_id: string | null;
  stage_id: number;
  /** The name, or null when the per-stage permission arm refused it (D65). */
  stage_name: string | null;
  /**
   * ⚠️ NEVER NULL, and that is bought in SQL rather than here. The ported arm
   * evaluates to SQL NULL on 26 of 1,039 live stages (measured 2026-08-17), so
   * the function wraps it in `COALESCE(..., false)`. If this ever arrives null,
   * the migration was not applied and `stageCellFor` reads it as unknown.
   */
  name_withheld: boolean;
}

export type StageCell =
  | { kind: 'named'; name: string }
  | { kind: 'withheld' }
  | { kind: 'unknown' };

/**
 * ⚠️ LOAD-BEARING COPY, NOT CHROME. These two strings are the only place the
 * product distinguishes "you may not see this" from "we could not find out", and
 * the whole of F-50 is that the previous cell distinguished neither. Asserted in
 * `projectStageColumn.test.ts` so that a tidy-up of "just wording" fails a test,
 * the same way `mergedFilters.test.ts` and `mergedMetrics.test.ts` guard theirs.
 */
export const STAGE_WITHHELD_LABEL = 'Restricted';
export const STAGE_WITHHELD_NOTE =
  'This project is in a stage your role cannot open, so its name is not shown. The project has a stage.';

export const STAGE_UNKNOWN_LABEL = 'Unavailable';
export const STAGE_UNKNOWN_NOTE =
  'The stage could not be read for this row. Every project has a stage, so this is not the same as having none.';

/**
 * The cell for one project.
 *
 * `readFailed` is the call's outcome, not the row's: an RPC that errored makes
 * every row on that page unknown, because a page with no answer must not be
 * rendered as a page whose answer is "none".
 *
 * ⚠️ A MISSING ROW IS `unknown`, NEVER `named` AND NEVER SILENTLY EMPTY. The
 * function returns one row per project it can authorize, so a project the list
 * showed but the function omitted means the two reads disagreed about scope,
 * which is precisely the moment to say so rather than to guess.
 */
export function stageCellFor(
  row: ProjectStageRow | undefined,
  readFailed: boolean,
): StageCell {
  return stageNameCell(row, readFailed);
}

/**
 * SUB-4.18. The same decision, for a caller holding a row of
 * `merged_scope_stage_names(bigint[])` rather than of
 * `merged_scope_project_stages(uuid[])`.
 *
 * ⚠️ **ONE DEFINITION, TWO ENTRY POINTS.** `FollowedProjects` reads stages by
 * stage id and the Projects list reads them by project id, and both have to
 * reach the same three answers. Writing the branch twice is how one surface
 * comes to say "Restricted" where the other says "Unavailable".
 */
export function stageNameCell(
  row: { stage_name?: string | null; name_withheld?: boolean | null } | undefined,
  readFailed: boolean,
): StageCell {
  if (readFailed) return { kind: 'unknown' };
  if (!row) return { kind: 'unknown' };

  // ⚠️ AN EXPLICIT `false` IS REQUIRED TO PROCEED, not merely "not true". If the
  // flag is null the database did not answer, and falling through would show the
  // very name the policy would have withheld. The SQL cannot produce a null here
  // (the arm is wrapped in COALESCE for that reason), so this is what an
  // unapplied migration or a hand-rolled caller looks like.
  if (row.name_withheld !== false) {
    return row.name_withheld === true ? { kind: 'withheld' } : { kind: 'unknown' };
  }

  const name = typeof row.stage_name === 'string' ? row.stage_name.trim() : '';
  // Not withheld and yet nameless is a broken invariant, not an empty stage.
  // `stages.name` is NOT NULL, so this cannot happen against a healthy database
  // and must not be reported as though the project had no stage.
  if (name === '') return { kind: 'unknown' };

  return { kind: 'named', name };
}

/** What the cell renders. */
export function stageCellLabel(cell: StageCell): string {
  switch (cell.kind) {
    case 'named':
      return cell.name;
    case 'withheld':
      return STAGE_WITHHELD_LABEL;
    case 'unknown':
      return STAGE_UNKNOWN_LABEL;
  }
}

/** The cell's explanation, or null when the name speaks for itself. */
export function stageCellNote(cell: StageCell): string | null {
  switch (cell.kind) {
    case 'named':
      return null;
    case 'withheld':
      return STAGE_WITHHELD_NOTE;
    case 'unknown':
      return STAGE_UNKNOWN_NOTE;
  }
}

/**
 * The page's rows, keyed by project.
 *
 * ⚠️ MERGE, NEVER REPLACE, at the call site. The list appends pages, so a map
 * rebuilt from page 2 alone would blank page 1. This function builds one page's
 * worth; combining them is `Projects.tsx`'s job and it spreads the previous map.
 */
export function stageRowsById(
  rows: readonly ProjectStageRow[],
): Map<string, ProjectStageRow> {
  const byId = new Map<string, ProjectStageRow>();
  for (const row of rows) {
    if (row && typeof row.project_id === 'string') byId.set(row.project_id, row);
  }
  return byId;
}

/**
 * Is this an array of `merged_scope_project_stages` rows?
 *
 * ⚠️ PRESENCE, NOT NON-NULLNESS, for the nullable column, and a `some()` rather
 * than the `every()` that cost SUB-4.11 a story: a malformed row here must cost
 * that row its name, never the whole page its stages. Rows that fail are dropped
 * and their projects read as unknown.
 */
export function isProjectStageRow(value: unknown): value is ProjectStageRow {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.project_id === 'string' &&
    'stage_name' in row &&
    typeof row.name_withheld === 'boolean'
  );
}
